/**
 * ContextChipsBar — three persistent context chips at the top of
 * every chat thread (Workspace · Doctype · Library).
 *
 * Replaces the legacy ContextStrip-based breadcrumb chips at
 * VariantCShell.tsx:2910-2937 (per Spec 34 §"Mapping"). The
 * library chip's click opens the LibraryHubSlideOver in
 * library-list mode for unified library selection and management.
 * Workspace + doctype chip clicks defer to existing pickers.
 *
 * Single-responsibility: this component is purely presentation +
 * routing; no fetch, no state shape decisions, no integration
 * knowledge of the broader VariantCShell. The shell wires:
 *   onWorkspaceClick   → existing WorkspacePicker open
 *   onDoctypeClick     → existing DoctypePicker open
 *   onManageLibraries  → opens LibraryHubSlideOver in library-list mode
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import type { Library, Workspace } from "../../../types";
import { fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import { Chip } from "./Chip";

export interface ContextChipsBarProps {
  /** Active workspace; rendered as the first chip. */
  workspace: Workspace | null;
  onWorkspaceClick: () => void;

  /** Active doctype display name; null/empty → italic placeholder. */
  doctypeLabel: string | null;
  onDoctypeClick: () => void;

  /** Active library — rendered as the third chip. */
  activeLibrary: Library | null;

  /** Opens the LibraryHubSlideOver in library-list mode for
   *  library selection, inspection, and creation. */
  onManageLibraries: () => void;
  /** When true, the library chip is non-interactive (doctype not yet chosen). */
  libraryDisabled?: boolean;
  /** Tooltip shown on the disabled library chip. */
  libraryDisabledTooltip?: string;
}

const useStyles = makeStyles({
  bar: {
    flexShrink: 0,
    position: "relative",
    padding: "9px 14px",
    backgroundColor: "white",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: surfaceColors.line,
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "::-webkit-scrollbar": {
      display: "none",
    },
    "@media (max-width: 520px)": {
      padding: "8px 10px",
      columnGap: "6px",
    },
  },
  separator: {
    color: inkColors.ink5,
    fontFamily: fonts.mono,
    fontSize: "11px",
    userSelect: "none",
    opacity: 0.9,
  },
});

/** Status icon for the library chip — green check when signed,
 *  amber dot when there are pending classifications, nothing when
 *  no library is picked. Pure render, no state. */
function libraryStatusIcon(library: Library | null): React.ReactNode {
  if (!library) return null;
  if (library.signed) {
    return (
      <span
        aria-label="signed"
        style={{
          width: "9px",
          height: "9px",
          borderRadius: "50%",
          backgroundColor: statusColors.cool,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="6" height="6" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M3.5 6.5l1.5 1.5L8.5 4.5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return (
    <span
      aria-label="pending review"
      style={{
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: statusColors.warn,
        display: "inline-block",
      }}
    />
  );
}

export const ContextChipsBar: React.FC<ContextChipsBarProps> = ({
  workspace,
  onWorkspaceClick,
  doctypeLabel,
  onDoctypeClick,
  activeLibrary,
  onManageLibraries,
  libraryDisabled = false,
  libraryDisabledTooltip,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.bar} data-testid="context-chips-bar">
      <Chip
        label={workspace?.name ?? null}
        placeholder="pick a workspace"
        onClick={onWorkspaceClick}
        ariaLabel="workspace"
      />
      <span className={styles.separator}>·</span>
      <Chip label={doctypeLabel} placeholder="pick a doctype" onClick={onDoctypeClick} ariaLabel="doctype" />
      <span className={styles.separator}>·</span>
      <Chip
        label={activeLibrary?.name ?? null}
        placeholder="pick a library"
        onClick={libraryDisabled ? undefined : onManageLibraries}
        icon={libraryStatusIcon(activeLibrary)}
        ariaLabel="library"
        disabled={libraryDisabled}
        tooltip={libraryDisabled ? libraryDisabledTooltip : undefined}
      />
    </div>
  );
};

export default ContextChipsBar;
